import {cn} from '@common-web/lib/utils';
import {Input} from '@common-web/components/ui/input';
import {Field, TextField, type SectionProps} from '../controls';
import {groupRequiresAccessCode, type PlanConfig} from '@common/enrollmentSite/types';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';

// IDs use text inputs: a number input steps its value on scroll.
const digitsOnly = (v: string) => v.replace(/\D/g, '');

export function E123Section({config, e123, patchE123}: SectionProps) {
  const plansById = new Map(config.plans.map((p) => [p.id, p]));
  const requiresCode = groupRequiresAccessCode(config);
  const missingCount = config.plans.filter((p) => e123.planPdid[p.id] === undefined).length;

  function setPlanPdid(planId: string, val: string) {
    const next = {...e123.planPdid};
    if (val.trim() === '') delete next[planId];
    else next[planId] = Number(val);
    patchE123({planPdid: next});
  }

  return (
    <div className='space-y-6'>
      <p className='text-sm text-muted-foreground'>
        Maps this site’s plans to their e123 products for submission. Every plan needs a PDID
        before the site can go live.
      </p>

      <div className='grid gap-4 sm:grid-cols-2'>
        <Field
          label='Billing period'
          hint={`e123 period id, set by Pay period in Products & plans (${sitePayPeriod(config).label}).`}
        >
          <Input type='number' value={e123.periodId ?? ''} readOnly disabled />
        </Field>
        <TextField
          label='Effective date'
          hint='Plan-year start, mm/dd/yyyy.'
          value={e123.effectiveDate}
          onChange={(v) => patchE123({effectiveDate: v})}
        />
      </div>

      <Field
        label='Plan PDIDs'
        hintTop
        hint={
          config.plans.length === 0
            ? 'Add plans in Products & plans first.'
            : missingCount > 0
              ? `${missingCount} plan${missingCount === 1 ? '' : 's'} still need a PDID.`
              : 'All plans mapped.'
        }
      >
        <div className='space-y-4'>
          {config.products.map((product) => {
            const productPlans = product.planIds
              .map((id) => plansById.get(id))
              .filter((p): p is PlanConfig => !!p);
            if (productPlans.length === 0) return null;
            return (
              <div key={product.category} className='overflow-hidden rounded-lg border'>
                <div className='border-b bg-muted/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  {product.label || product.category}
                </div>
                <div className='divide-y'>
                  {productPlans.map((plan) => {
                    const pdid = e123.planPdid[plan.id];
                    return (
                      <label key={plan.id} className='flex items-center gap-3 px-3 py-2 text-sm'>
                        <span
                          className='h-2.5 w-2.5 shrink-0 rounded-full'
                          style={{backgroundColor: plan.accentColor}}
                        />
                        <span className='flex-1 truncate'>{plan.name || 'Untitled plan'}</span>
                        <Input
                          inputMode='numeric'
                          className={cn('h-8 w-32', pdid === undefined && 'border-amber-400')}
                          placeholder='PDID'
                          value={pdid ?? ''}
                          onChange={(e) => setPlanPdid(plan.id, digitsOnly(e.target.value))}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Field>

      <div className='grid gap-4 sm:grid-cols-2'>
        <Field label='Decline PDID' hint='e123 product recorded when medical is declined.'>
          <Input
            inputMode='numeric'
            placeholder='PDID'
            value={e123.declinePdid ?? ''}
            onChange={(e) => {
              const v = digitsOnly(e.target.value);
              patchE123({declinePdid: v === '' ? undefined : Number(v)});
            }}
          />
        </Field>
        <Field label='Decline period' hint='Defaults to the billing period.'>
          <Input
            inputMode='numeric'
            placeholder={String(e123.periodId ?? '')}
            value={e123.declinePeriodId ?? ''}
            onChange={(e) => {
              const v = digitsOnly(e.target.value);
              patchE123({declinePeriodId: v === '' ? undefined : Number(v)});
            }}
          />
        </Field>
      </div>

      {requiresCode ? (
        <Field label='Agent'>
          <p className='rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground'>
            Code-gated group — the e123 agent is resolved per location, not set here.
          </p>
        </Field>
      ) : (
        <TextField
          label='Agent ID'
          hint='Fixed e123 agent for this public (no-code) group.'
          value={e123.agentId}
          onChange={(v) => patchE123({agentId: v || undefined})}
        />
      )}
    </div>
  );
}
